import React, { useState } from 'react';

import classNames from 'classnames';

import { Label } from '../types';

type Props = {
  labels: Label[];
  selectedIds: string[];
  onToggle: (labelId: string) => void;
  onCreate: (name: string) => Promise<void>;
  onManage?: () => void;
  extraClassName?: string;
};

/**
 * The checkbox list behind the "Labels" button in the thread header. Archived
 * labels are not offered — they only live on as chips on the conversations
 * that already carry them.
 */
const LabelPicker: React.FC<Props> = ({
  labels,
  selectedIds,
  onToggle,
  onCreate,
  onManage,
  extraClassName,
}: Props) => {
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const active = labels.filter((label) => !label.archived);

  const create = async (): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed || isSaving) return;
    setIsSaving(true);
    try {
      await onCreate(trimmed);
      setName('');
    } catch {
      // Keep what they typed: the caller already told them the save failed.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={classNames(
        'flex flex-col overflow-y-auto overflow-x-hidden max-h-80 w-64 bg-white-0.0 shadow rounded absolute z-50 py-3',
        extraClassName
      )}
    >
      <p className="text-xs text-gray-1.0 font-semibold pb-2 px-4 uppercase">Labels</p>
      {active.map((label) => (
        <label
          key={label.id}
          className="flex items-center gap-2 px-4 py-1.5 text-sm cursor-pointer text-black-1.0 hover:bg-white-1.0"
        >
          <input
            type="checkbox"
            checked={selectedIds.includes(label.id)}
            onChange={() => onToggle(label.id)}
          />
          <span className="truncate">{label.name}</span>
        </label>
      ))}
      {active.length === 0 && <p className="px-4 py-2 text-sm text-gray-1.0">None yet.</p>}

      <div className="flex gap-2 px-4 pt-2 mt-1 border-t border-white-1.0">
        <label className="sr-only" htmlFor="support-new-label">
          New label
        </label>
        <input
          id="support-new-label"
          type="text"
          value={name}
          maxLength={80}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void create();
            }
          }}
          placeholder="New label…"
          className="flex-1 min-w-0 px-2 py-1 text-sm border rounded border-white-3.0 focus:outline-none"
        />
        <button
          type="button"
          disabled={!name.trim() || isSaving}
          onClick={() => void create()}
          className="px-2 py-1 text-sm rounded bg-primary-2.0 text-white-0.0 disabled:opacity-50"
        >
          Add
        </button>
      </div>

      {onManage && (
        <button
          type="button"
          className="px-4 pt-2 mt-2 text-sm font-medium text-left border-t text-primary-2.0 border-white-1.0 hover:underline"
          onClick={onManage}
        >
          Manage labels
        </button>
      )}
    </div>
  );
};

export default LabelPicker;
